import React, { useState, useEffect, useCallback } from "react";
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import UserDetails from './UserDetails'; // Import the new component
import "./UserPage.css";
import { io } from "socket.io-client";
import axios from "axios";
import { toast } from "sonner";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

const Loader = ({ message = "Loading, please wait..." }) => {
  return (
    <div className="loader-container">
      <div className="spinner"></div>
      <p>{message}</p>
    </div>
  );
};

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const UsersPage = () => {
  const API_BASE_URL = `${process.env.REACT_APP_SERVER_URL}`;
  const token = localStorage.getItem('token')
  const [activeUserCount, setActiveUserCount] = useState(0);

  // --- State Variables ---
  const [allUsers, setAllUsers] = useState([]);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState('id');
  const [sortOrder, setSortOrder] = useState('asc');
  const [sortBy, setSortBy] = useState('status'); // 'status' | 'name' | 'joinedDate'
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' | 'desc'
  const [resetPasswords, setResetPasswords] = useState({});
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [invitedUsers, setInvitedUsers] = useState([]);
  const [loadingInvites, setLoadingInvites] = useState(false);
  const [inviteType, setInviteType] = useState("email");
  const [phone, setPhone] = useState("");

  const [inviteValue, setInviteValue] = useState("");

  const navigate = useNavigate();

  const showErrorNotification = (message) => {
    console.error(message);
    toast.error(message); // you can replace later
  };

  const showSuccessNotification = (message) => {
    console.log(message);
    toast.success(message);
  };

  // When user clicks on a user row/card
  const handleUserClick = (userId) => {
    navigate(`/dashboard/user/${userId}/details`);
  };
  // New state for user details view
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [showUserDetails, setShowUserDetails] = useState(false);

  // Debounce the search term
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [showResetPasswordFor, setShowResetPasswordFor] = useState(null);

  // Function to apply pagination and filtering
  const applyPaginationAndFilter = useCallback(() => {
    let filteredUsers = [...allUsers];

    // Apply search filter
    if (debouncedSearchTerm) {
      filteredUsers = filteredUsers.filter(user =>
        user.name?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
        user.phone?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
        user.role?.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
      );
    }

    // Apply sorting based on active sortBy
    if (sortBy === 'name') {
      filteredUsers.sort((a, b) => {
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();
        if (sortDirection === 'asc') {
          return nameA.localeCompare(nameB);
        } else {
          return nameB.localeCompare(nameA);
        }
      });
    } else if (sortBy === 'joinedDate') {
      filteredUsers.sort((a, b) => {
        const dateA = new Date(a.createdAt || 0).getTime();
        const dateB = new Date(b.createdAt || 0).getTime();
        if (sortDirection === 'asc') {
          // Newest first (descending by date = 'asc' click)
          return dateB - dateA;
        } else {
          // Oldest first
          return dateA - dateB;
        }
      });
    } else {
      // Default: sort by status priority
      filteredUsers.sort((a, b) => getStatusPriority(a.status) - getStatusPriority(b.status));
    }

    // Update total count
    setTotalUsers(filteredUsers.length);

    // Apply pagination
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

    setUsers(paginatedUsers);
  }, [allUsers, debouncedSearchTerm, page, limit, sortBy, sortDirection]);

  // Apply pagination when dependencies change
  useEffect(() => {
    applyPaginationAndFilter();
  }, [applyPaginationAndFilter]);
  // const [userstatus, setUsers] = useState([]);

  // useEffect(() => {
  //   const socket = io('https://medhas.ai');

  //   socket.on('connect', () => {
  //     socket.emit('admin-login');
  //   });

  //   socket.on('all-user-statuses', (data) => {
  //     console.log(data, 'data--admin section')
  //     const activeCount = data.filter(user => user.status === 'active').length;
  //     // 🟢 Update the new state variable
  //     setActiveUserCount(activeCount);
  //      const getStatusPriority = (status) => {
  //       switch (status) {
  //         case 'active':
  //           return 1;
  //         case 'inactive':
  //           return 2;
  //         case 'logged_out':
  //           return 3;
  //         default:
  //           return 4; // For any other status
  //       }
  //     };

  //     // 🟢 Step 3: Sort the data based on status priority
  //     const sortedData = [...data].sort((a, b) => {
  //       const priorityA = getStatusPriority(a.status);
  //       const priorityB = getStatusPriority(b.status);
  //       return priorityA - priorityB;
  //     });

  //     setUsers(sortedData);
  //     setIsLoading(!isLoading)
  //   });

  //   return () => {
  //     socket.disconnect();
  //   };
  // }, []);
  const getStatusPriority = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 1;
      case 'inactive':
      case 'idle':
        return 2;
      case 'logged_out':
      case 'logged out':
        return 3;
      case 'not logged in':
        return 4;
      case 'deleted':
        return 5; // Deleted users appear last
      default:
        return 4;
    }
  };

  const handleResendInvite = async (user) => {

    try {

      const response = await axios.post(

        `${API_BASE_URL}/api/admin/invite-user`,

        {
          type: user.type,
          value: user.value,
        },

        {
          withCredentials: true,
        }
      );

      toast.success(response.data.message);

      fetchInvitedUsers();

    } catch (error) {

      toast.error(
        error.response?.data?.message
      );
    }
  };

  useEffect(() => {
    // Fetch initial user data from API to get role information
    const fetchInitialUsers = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/admin/getallusers`, {
          method: 'GET',
          credentials: 'include',
        });

        if (response.ok) {
          const data = await response.json();
          console.log('Initial users from API:', data);

          // ✅ CRITICAL FIX: Force ALL users to 'logged_out' from REST API.
          // DB status is NEVER reliable for live display.
          // The socket 'all-user-statuses' event is the ONLY source of truth for status.
          const processedUsers = data.map(user => ({
            ...user,
            status: user.isDeleted ? 'Deleted' : 'logged_out',  // Always start as logged_out
          }));

          setAllUsers(processedUsers);
          setActiveUserCount(0); // Start at 0 — socket will update to real count
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error fetching initial users:', error);
        setIsLoading(false);
      }
    };

    fetchInitialUsers();

    const socket = io(`${process.env.REACT_APP_SERVER_URL}`);

    socket.on('connect', () => {
      // First request: immediate snapshot (may show logged_out if user sockets haven't reconnected yet)
      socket.emit('admin-login');

      // Second request: after 1.5s, all user sockets will have reconnected — gives accurate status
      setTimeout(() => {
        if (socket.connected) {
          socket.emit('admin-login');
        }
      }, 1500);
    });

    socket.on('connect_error', (error) => {
      console.error('❌ Socket connection error:', error);
    });

    socket.on('disconnect', (reason) => {
      console.log('⚠️ Admin socket disconnected:', reason);
    });

    // ✅ FIX: This single listener now handles both array (initial) and object (live) data
    socket.on('all-user-statuses', (data) => {
      console.log(data, 'data--admin section');

      if (Array.isArray(data)) {
        // -------- A. INITIAL FULL LOAD (Array from socket) --------
        // Socket data is the LIVE source of truth for status.
        // Merge it with REST API data (which has role, phone, isDeleted etc.)
        const socketUsers = data;

        const activeCount = socketUsers.filter(user => user.status?.toLowerCase() === 'active').length;
        setActiveUserCount(activeCount);

        setAllUsers(prevUsers => {
          // Helper: get a comparable string ID from any user object
          const getId = (u) => (u?._id || u?.id || '').toString();

          // For each socket user, find their full data (role, phone etc.) from REST state
          const mergedUsers = socketUsers.map(socketUser => {
            const socketId = getId(socketUser);
            const existingUser = prevUsers.find(u => getId(u) === socketId);

            if (existingUser) {
              return {
                ...existingUser,                   // Keep role, phone, email etc. from REST
                status: existingUser.isDeleted ? 'Deleted' : socketUser.status, // Live status from socket
                lastActive: socketUser.lastActive,
                _id: socketId,  // Normalise to string
              };
            }
            // User not in REST data but in socket — use socket data as-is
            return { ...socketUser, _id: socketId };
          });

          // Keep any REST users that socket didn't include (show as logged_out)
          prevUsers.forEach(restUser => {
            const restId = getId(restUser);
            if (restId && !mergedUsers.find(u => getId(u) === restId)) {
              mergedUsers.push({ ...restUser, _id: restId }); // already logged_out from REST fetch
            }
          });

          return mergedUsers.sort((a, b) => getStatusPriority(a.status) - getStatusPriority(b.status));
        });

      } else if (typeof data === 'object' && data !== null) {
        // -------- B. LIVE SINGLE USER UPDATE (Object) --------
        // A single user's status changed in real-time
        const updatedUser = data;
        const updatedId = (updatedUser._id || updatedUser.id || '').toString();

        setAllUsers(prevUsers => {
          let userFound = false;

          // 1. Find and Update the user in the list
          const newUsers = prevUsers.map(user => {
            const userId = (user._id || user.id || '').toString();
            if (userId === updatedId) {
              userFound = true;
              return {
                ...user,
                status: user.isDeleted ? 'Deleted' : updatedUser.status,
                lastActive: updatedUser.lastActive,
              };
            }
            return user;
          });

          // 2. If user not found in list, add them (new connection from another account)
          if (!userFound && updatedId) {
            newUsers.push({ ...updatedUser, _id: updatedId, role: 'user', isAdmin: 'no' });
          }

          // 3. Recalculate Active Count and Re-sort
          const activeCount = newUsers.filter(user => user.status?.toLowerCase() === 'active').length;
          setActiveUserCount(activeCount);

          return [...newUsers].sort((a, b) => getStatusPriority(a.status) - getStatusPriority(b.status));
        });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [API_BASE_URL]);

  const formatTimeAgo = (date) => {
    if (!date) return 'N/A';
    const now = new Date();
    const then = new Date(date);
    const seconds = Math.floor((now - then) / 1000);

    if (seconds < 60) return `${seconds} seconds ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
    return `${Math.floor(seconds / 86400)} days ago`;
  };


  // --- API Fetching Logic ---
  //   const fetchUsersData = useCallback(async () => {
  //     setIsLoading(true);
  //     setError(null);

  //     try {
  //         const url = new URL(`${API_BASE_URL}/api/admin/getallusers`);
  //         url.searchParams.append('_page', page);
  //         url.searchParams.append('_limit', limit);
  //         if (debouncedSearchTerm) {
  //             url.searchParams.append('q', debouncedSearchTerm);
  //         }
  //         url.searchParams.append('_sort', sortColumn);
  //         url.searchParams.append('_order', sortOrder);

  //         const response = await fetch(url.toString(), {
  //             method: 'GET',
  //             headers: {
  //                 'Content-Type': 'application/json',
  //                 'Authorization': `Bearer ${token}`
  //             }
  //         });

  //         const totalCountHeader = response.headers.get('X-Total-Count');
  //         const data = await response.json();

  //         if (!response.ok) {
  //             throw new Error(`HTTP error! Status: ${response.status} - ${response.statusText || 'Unknown Error'}. Message: ${data.message || 'No specific error message from server.'}`);
  //         }

  //         setUsers(data);
  //         setTotalUsers(parseInt(totalCountHeader, 10) || 0);
  //     } catch (err) {
  //         console.error("Failed to fetch users:", err);
  //         setError(err);
  //         Swal.fire({
  //             icon: 'error',
  //             title: 'Error!',
  //             text: `Failed to load users: ${err.message || 'Please check your network.'}`,
  //             confirmButtonColor: '#dc3545',
  //         });
  //     } finally {
  //         setIsLoading(false);
  //     }
  // }, [page, limit, debouncedSearchTerm, sortColumn, sortOrder, API_BASE_URL, token]);

  //   // --- Effect Hook to trigger data fetching ---
  //   useEffect(() => {
  //     fetchUsersData();
  //   }, [fetchUsersData]);

  // --- Navigation Handlers ---
  const handleViewUserDetails = (userId) => {
    setSelectedUserId(userId);
    setShowUserDetails(true);
  };

  const handleBackToUsersList = () => {

    setShowUserDetails(false);
    setSelectedUserId(null);
    // Refresh the users list
    // fetchUsersData();
  };

  // --- Pagination Handlers ---
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleLimitChange = (event) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(1);
  };

  // --- Search Handler ---
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(1);
  };

  // --- Sorting Handlers ---
  const handleSort = (column) => {
    const isAsc = sortColumn === column && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortColumn(column);
  };

  // Handle sorting for Name and Joined Date columns
  const handleColumnSort = (column) => {
    if (sortBy === column) {
      // Toggle direction if same column clicked again
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
    setPage(1);
  };

  // Reset to default status sorting
  const resetToDefaultSort = () => {
    setSortBy('status');
    setSortDirection('asc');
  };

  // Helper for rendering sort indicator in table header
  const renderSortIndicator = (column) => {
    if (sortColumn === column) {
      return <span className={`sort-icon ${sortOrder}`}></span>;
    }
    return null;
  };

  // Helper for rendering sort indicator for Name and Joined Date
  const renderColumnSortIndicator = (column) => {
    if (sortBy === column) {
      return <span className={`sort-icon ${sortDirection}`}></span>;
    }
    return null;
  };

  // Format joined date
  const formatJoinedDate = (date) => {
    if (!date) return 'N/A';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Handle Reset Password
  const handleResetPassword = (user) => {
    Swal.fire({
      title: 'Reset Password?',
      text: `Are you sure you want to reset the password for ${user.name}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ff6b35',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, reset it!',
      cancelButtonText: 'Cancel'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setResetPasswords({});
          setShowResetPasswordFor(null);

          const response = await fetch(`${API_BASE_URL}/api/admin/reset-password`, {
            method: 'PUT',
            credentials: 'include',
            // headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({
              userId: user.id,
              email: user.email
            }),
          });

          if (!response.ok) {
            throw new Error(`Failed to reset password: ${response.statusText}`);
          }

          setShowResetPasswordFor(user.id);
          const responseData = await response.json();

          if (!responseData.newPassword) {
            throw new Error('No password received from server');
          }

          setResetPasswords(prev => ({
            ...prev,
            [user.id]: responseData.newPassword
          }));

          setShowResetPasswordFor(user.id);

          await Swal.fire({
            title: 'Password Reset Successful!',
            html: `
              <p>New password has been generated.</p>
              <div style="margin: 10px; padding: 10px; background: #f8f9fa; border-radius: 4px;">
                <strong>New Password:</strong><br>
                <span style="color: #28a745; font-weight: bold; font-size: 1.2em;">
                  ${responseData.newPassword}
                </span>
              </div>
              <p style="font-size: 0.9em; color: #6c757d;">
                This password will also be sent to the user's email.
              </p>
            `,
            icon: 'success',
            confirmButtonColor: '#28a745'
          });
        } catch (resetError) {
          console.error('Error resetting password:', resetError);
          await Swal.fire('Error!', `Failed to reset password: ${resetError.message}`, 'error');
        }
      }
    });
  };

  const handleAddUser = () => {
    Swal.fire({
      title: 'Add New User',
      html: `
        <input id="swal-input-name" class="swal2-input" placeholder="Name" required>
        <input id="swal-input-email" class="swal2-input" type="email" placeholder="Email" required>
        <input id="swal-input-phone" class="swal2-input" placeholder="Phone">
        <input id="swal-input-website" class="swal2-input" placeholder="Website">
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Add User',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#28a745',
      cancelButtonColor: '#6c757d',
      preConfirm: () => {
        const name = Swal.getPopup().querySelector('#swal-input-name').value;
        const email = Swal.getPopup().querySelector('#swal-input-email').value;
        const phone = Swal.getPopup().querySelector('#swal-input-phone').value;
        const website = Swal.getPopup().querySelector('#swal-input-website').value;

        if (!name.trim() || !email.trim()) {
          Swal.showValidationMessage('Name and Email are required!');
          return false;
        }
        return { name, email, phone, website };
      }
    }).then(async (result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: 'Adding User...',
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });
        try {
          const response = await fetch(`${API_BASE_URL}/users`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(result.value),
          });
          if (!response.ok) {
            throw new Error(`Failed to add user: ${response.statusText}`);
          }
          await Swal.fire('Success!', 'User has been added.', 'success');
          // fetchUsersData();
        } catch (addError) {
          console.error('Error adding user:', addError);
          await Swal.fire('Error!', `Failed to add user: ${addError.message}`, 'error');
        }
      }
    });
  };

  const handleEdit = (user) => {
    Swal.fire({
      title: `Edit User: ${user.name}`,
      html: `
        <input id="swal-input-name" class="swal2-input" value="${user.name}" placeholder="Name" required>
        <input id="swal-input-email" class="swal2-input" type="email" value="${user.value}" placeholder="Email" required>
        <input id="swal-input-phone" class="swal2-input" value="${user.phone}" placeholder="Phone">
        <input id="swal-input-website" class="swal2-input" value="${user.website}" placeholder="Website">
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Update User',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#28a745',
      cancelButtonColor: '#6c757d',
      preConfirm: () => {
        const name = Swal.getPopup().querySelector('#swal-input-name').value;
        const email = Swal.getPopup().querySelector('#swal-input-email').value;
        const phone = Swal.getPopup().querySelector('#swal-input-phone').value;
        const website = Swal.getPopup().querySelector('#swal-input-website').value;

        if (!name.trim() || !email.trim()) {
          Swal.showValidationMessage('Name and Email are required!');
          return false;
        }
        return { name, email, phone, website };
      }
    }).then(async (result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: 'Updating User...',
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });
        try {
          const response = await fetch(`${API_BASE_URL}/users/${user.id}`, {
            method: 'PUT',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: user.id, ...result.value }),
          });
          if (!response.ok) {
            throw new Error(`Failed to update user: ${response.statusText}`);
          }
          await Swal.fire('Updated!', 'User has been updated.', 'success');
          // fetchUsersData();
        } catch (updateError) {
          console.error('Error updating user:', updateError);
          await Swal.fire('Error!', `Failed to update user: ${updateError.message}`, 'error');
        }
      }
    });
  };

  const handleDelete = (userId) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "This will soft delete the user. You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: 'Deleting User...',
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });
        try {
          console.log('Attempting to delete user with ID:', userId);
          console.log('API URL:', `${API_BASE_URL}/api/admin/delete-user/${userId}`);

          const response = await axios.delete(`${API_BASE_URL}/api/admin/delete-user/${userId}`, {
            credentials: 'include',
            // headers: {
            //   'Content-Type': 'application/json',
            //   'Authorization': `Bearer ${token}`
            // }
          });

          console.log('Response status:', response);
          console.log('Response ok:', response);

          if (response.status !== 200) {
            const errorData = await response.data.catch(() => ({}));
            console.log('Error data:', errorData);
            throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
          }

          const data = await response.data;
          console.log('Success data:', data);

          // Update the local state to mark user as deleted
          setAllUsers(prevUsers =>
            prevUsers.map(user =>
              (user.id === userId || user._id === userId)
                ? { ...user, isDeleted: true, deletedAt: new Date().toISOString(), status: 'Deleted' }
                : user
            )
          );

          // Clear the reset password for this user when deleted
          setResetPasswords(prev => {
            const updated = { ...prev };
            delete updated[userId];
            return updated;
          });

          await Swal.fire({
            icon: 'success',
            title: 'Deleted!',
            text: data.message || 'User has been deleted successfully.',
            timer: 2000,
            showConfirmButton: false
          });
        } catch (deleteError) {
          console.error('Error deleting user:', deleteError);
          await Swal.fire(
            'Error!',
            `Failed to delete user: ${deleteError.message}`,
            'error'
          );
        }
      }
    });
  };

  const handleRoleChange = async (userId, currentRole, event) => {
    event.stopPropagation();
    const newRole = event.target.value;

    if (newRole === currentRole) return;

    // Show confirmation dialog
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Do you want to change the user's role from "${currentRole}" to "${newRole}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, change it!',
      cancelButtonText: 'Cancel'
    });

    // If user cancels, revert the dropdown and return
    if (!result.isConfirmed) {
      event.target.value = currentRole;
      return;
    }

    try {
      const response = await axios.put(
        `${API_BASE_URL}/api/admin/update-user-role`,
        {
          userId: userId,
          role: newRole,
          isAdmin: newRole === 'admin' ? 'yes' : 'no'
        },
        {
          withCredentials: true, // important for httpOnly cookie
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      // Update local state
      setAllUsers(prevUsers =>
        prevUsers.map(user =>
          (user.id === userId || user._id === userId)
            ? {
              ...user,
              role: newRole,
              isAdmin: newRole === 'admin' ? 'yes' : 'no'
            }
            : user
        )
      );

      await Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'User role updated successfully',
        timer: 2000,
        showConfirmButton: false
      });

    } catch (error) {
      console.error('Error updating role:', error);

      await Swal.fire(
        'Error!',
        `Failed to update role: ${error.response?.data?.message || error.message}`,
        'error'
      );

      // Revert dropdown to original value
      event.target.value = currentRole;
    }

  };

  // Calculate total pages for pagination
  const totalPages = Math.ceil(totalUsers / limit);

  // const handleInviteUser = async () => {
  //   const email = inviteEmail.trim().toLowerCase();

  //   // ✅ Validation
  //   if (!email) {
  //     showErrorNotification("Email is required");
  //     return;
  //   }

  //   const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  //   if (!emailRegex.test(email)) {
  //     showErrorNotification("Invalid email format");
  //     return;
  //   }

  //   try {
  //     setInviting(true);

  //     const response = await axios.post(
  //       `${process.env.REACT_APP_SERVER_URL}/api/admin/invite-user`,
  //       { email },
  //       {
  //         withCredentials: true
  //       }
  //     );

  //     // ✅ Success
  //     showSuccessNotification(
  //       response.data.message || "Invitation sent successfully"
  //     );

  //     // Reset UI
  //     await fetchInvitedUsers();
  //     setInviteEmail("");
  //     // setShowInviteModal(false);

  //   } catch (error) {
  //     console.error("Invite error:", error);

  //     const message =
  //       error.response?.data?.message ||
  //       error.message ||
  //       "Failed to send invite";

  //     showErrorNotification(message);

  //   } finally {
  //     setInviting(false);
  //   }
  // };
  const handleInviteUser = async () => {

    try {

      setInviting(true);

      const payload = {

        type: inviteType,

        value:
          inviteType === "email"
            ? inviteValue.toLowerCase()
            : `+${phone}`,
      };

      const response = await axios.post(

        `${process.env.REACT_APP_SERVER_URL}/api/admin/invite-user`,

        payload,

        {
          withCredentials: true,
        }
      );

      toast.success(response.data.message);

      setInviteValue("");
      setPhone("");

      fetchInvitedUsers();

    } catch (error) {

      toast.error(
        error.response?.data?.message ||
        "Failed to send invite"
      );

    } finally {

      setInviting(false);
    }
  };
  useEffect(() => {
    if (showInviteModal) {
      fetchInvitedUsers();
    }
  }, [showInviteModal]);

  const fetchInvitedUsers = async () => {
    try {
      setLoadingInvites(true);

      const res = await axios.get(
        `${API_BASE_URL}/api/admin/invite-users`,
        { withCredentials: true }
      );

      // ⏳ Force 2 sec delay (UX purpose)
      setTimeout(() => {
        setInvitedUsers(res.data.data || []);
        setLoadingInvites(false);
      }, 2000);

    } catch (err) {
      console.error("Error fetching invites:", err);
      setLoadingInvites(false);
    }
  };

  // If showing user details, render the UserDetails component
  if (showUserDetails) {
    return (
      <UserDetails
        userId={selectedUserId}
        onBack={handleBackToUsersList}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />
    );
  }

  return (
    <div className="users-page-container">
      <div className="page-header">
        <h3>Users</h3>
        {/* <button className="add-user-button" onClick={handleAddUser}>
          <span style={{ fontSize: '1em' }}>+</span> Add New User
        </button> */}
        <div className="active-users-card">
          <h3 className="active-users-title">Active Users</h3>
          <p className="active-users-count">{activeUserCount}</p>
        </div>
      </div>

      <div className="table-controls">
        <input
          type="text"
          placeholder="Search users..."
          className="search-input"
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <button
          className="invite-btn"
          onClick={() => setShowInviteModal(true)}
        >
          Invite User
        </button>
      </div>

      {/* Conditional Rendering: Loader, Error, or Table */}
      {isLoading ? (
        <Loader message="Fetching user data..." />
      ) : error && users.length === 0 ? (
        <div className="error-message">
          <p>We couldn't load any users.</p>
          <p>Details: {error.message || 'Unknown error'}</p>
          <p>Please check your internet connection or try again later.</p>
        </div>
      ) : (
        <div className="table-wrapper">
          <table className="users-table">
            <thead>
              <tr>
                <th onClick={() => handleColumnSort('name')} className="sortable-header" style={{ cursor: 'pointer' }}>
                  Name {renderColumnSortIndicator('name')}
                </th>
                <th>
                  Email
                </th>
                <th onClick={resetToDefaultSort} className="sortable-header" style={{ cursor: 'pointer' }}>
                  Status {sortBy === 'status' ? <span className="sort-icon asc"></span> : null}
                </th>
                <th>
                  Phone
                </th>
                <th>
                  Role Permission
                </th>
                {/* <th onClick={() => handleSort('website')} className="">
                  Website {renderSortIndicator('website')}
                </th> */}
                <th>Actions</th>
                <th onClick={() => handleColumnSort('joinedDate')} className="sortable-header" style={{ cursor: 'pointer' }}>
                  Joined Date {renderColumnSortIndicator('joinedDate')}
                </th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ textAlign: 'center', padding: '20px' }}>No users found matching your criteria.</td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr
                    key={user.id || user._id}
                    onClick={() => !user.isDeleted && handleUserClick(user.id || user._id)}
                    className={user.isDeleted ? 'deleted-user-row' : ''}
                    style={{
                      opacity: user.isDeleted ? 0.6 : 1,
                      cursor: user.isDeleted ? 'default' : 'pointer'
                    }}
                  >
                    <td
                      className={user.isDeleted ? "deleted-cell" : "clickable-cell"}
                      onClick={() => !user.isDeleted && handleViewUserDetails(user.id || user._id)}
                      style={{
                        cursor: user.isDeleted ? 'default' : 'pointer',
                        color: user.isDeleted ? '#6c757d' : '#007bff',
                        textDecoration: user.isDeleted ? 'line-through' : 'none'
                      }}
                      title={user.isDeleted ? "Deleted user" : "Click to view user details"}
                    >
                      {user.name}
                    </td>
                    <td style={{
                      color: user.isDeleted ? '#6c757d' : 'inherit',
                      textDecoration: user.isDeleted ? 'line-through' : 'none'
                    }}>{user.value}</td>
                    <td>
                      <span className={`status-badge ${user.status?.toLowerCase() || 'logged_out'}`}>
                        {user.status ? user.status.charAt(0).toUpperCase() + user.status.slice(1).replace('_', ' ') : 'Logged out'}
                      </span>
                    </td>
                    {/* <td>
                      <div className="password-cell">
                        <button 
                          className="reset-button" 
                          onClick={() => handleResetPassword(user)}
                          title="Click to reset password"
                        >
                          Reset
                        </button>
                        {resetPasswords[user.id] && (
                          <div className="reset-password-display">
                            <strong>New Password:</strong><br />
                            <span style={{ color: '#28a745', fontWeight: 'bold', display: 'block', marginTop: '5px' }}>
                              {resetPasswords[user.id]}
                            </span>
                          </div>
                        )}
                      </div>
                    </td> */}
                    <td>{user.phone}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <select
                        className="role-select"
                        value={user.role || 'user'}
                        onChange={(e) => handleRoleChange(user.id || user._id, user.role || 'user', e)}
                        onClick={(e) => e.stopPropagation()}
                        disabled={user.isDeleted}
                        style={{
                          padding: '5px 10px',
                          borderRadius: '4px',
                          border: '1px solid #ddd',
                          cursor: user.isDeleted ? 'not-allowed' : 'pointer',
                          backgroundColor: user.isDeleted ? '#f8f9fa' : (user.role === 'admin' ? '#ff6b35' : '#fff'),
                          color: user.isDeleted ? '#6c757d' : (user.role === 'admin' ? '#fff' : '#333'),
                          fontWeight: user.role === 'admin' ? 'bold' : 'normal',
                          opacity: user.isDeleted ? 0.6 : 1
                        }}
                      >
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                        <option value="customer care">Customer Care</option>
                        <option value="partner">Partner</option>
                        {/* <option value="emailer">Emailer</option> */}
                      </select>
                    </td>
                    {/* <td>{user.website}</td> */}
                    <td className="actions-cell">
                      {/* <button className="action-button view" onClick={() => handleViewUserDetails(user.id)} title="View Details">
                        👁️
                      </button> */}
                      {/* <button className="action-button edit" onClick={() => handleEdit(user)} title="Edit User">
                        &#9998;
                      </button> */}
                      <button
                        className={`action-button delete ${user.isDeleted ? 'disabled' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!user.isDeleted) {
                            handleDelete(user.id || user._id);
                          }
                        }}
                        title={user.isDeleted ? "User already deleted" : "Delete User"}
                        disabled={user.isDeleted}
                        style={{
                          opacity: user.isDeleted ? 0.5 : 1,
                          cursor: user.isDeleted ? 'not-allowed' : 'pointer'
                        }}
                      >
                        &#128465;
                      </button>
                    </td>
                    <td style={{
                      color: user.isDeleted ? '#6c757d' : '#555',
                      fontSize: '0.9em',
                      whiteSpace: 'nowrap'
                    }}>
                      {formatJoinedDate(user.createdAt)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          <div className="pagination-controls">
            <span className="pagination-info">
              Rows per page:
              <select className="rows-per-page-select" value={limit} onChange={handleLimitChange}>
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
              </select>
            </span>
            <button onClick={() => handlePageChange(page - 1)} disabled={page === 1}>
              Previous
            </button>
            <span className="pagination-info">
              Page {page} of {totalPages === 0 ? 1 : totalPages}
            </span>
            <button onClick={() => handlePageChange(page + 1)} disabled={page >= totalPages || totalUsers === 0}>
              Next
            </button>
          </div>
        </div>
      )}
      {showInviteModal && (
        <div className="invite-modal-overlay">
          <div className="invite-modal-box">
            <h3>Invite User</h3>

            <div className="invite-methods">

              <button
                className={inviteType === "email" ? "active" : ""}
                onClick={() => setInviteType("email")}
              >
                Email
              </button>

              <button
                className={inviteType === "sms" ? "active" : ""}
                onClick={() => setInviteType("sms")}
              >
                SMS
              </button>

              <button
                className={inviteType === "whatsapp" ? "active" : ""}
                onClick={() => setInviteType("whatsapp")}
              >
                WhatsApp
              </button>

            </div>

            {
              inviteType === "email" ? (

                <input
                  type="email"
                  placeholder="Enter email address"
                  value={inviteValue}
                  onChange={(e) => setInviteValue(e.target.value)}
                  className="invite-modal-input"
                />

              ) : (

                <PhoneInput
                  country={"in"}
                  value={phone}
                  onChange={(value) => setPhone(value)}
                  enableSearch={true}
                  countryCodeEditable={false}
                  inputClass="phone-input-field"
                  containerClass="phone-input-container"
                />

              )
            }

            <div className="invite-modal-actions">
              <button
                className="cancel-btn"
                onClick={() => {
                  setShowInviteModal(false);
                  setInviteEmail("");
                }}
              >
                Cancel
              </button>

              <button
                className="invite-submit-btn"
                onClick={handleInviteUser}
                disabled={inviting || (inviteType === "email" ? !inviteValue : !phone)}
              >
                {inviting ? "Sending..." : "Invite"}
              </button>
            </div>
            <div className="invited-users-section">
              <div className="invited-users-header">
                <h4>Invited Users</h4>
                <h4>({invitedUsers.length})</h4>
              </div>
              {loadingInvites ? (
                <div className="invite-loader">
                  <div className="spinner"></div>
                  <p>Fetching invited users...</p>
                </div>
              ) : invitedUsers.length === 0 ? (
                <p className="no-invites">No pending invites</p>
              ) : (
                <div className="invited-users-list">
                  {invitedUsers
                    .filter(
                      (user) => user.type === inviteType
                    ).map((user) => (
                      <div key={user._id} className="invite-user-row">

                        <div className="invite-user-email">
                          {user?.value} <span style={{ fontSize: '10px' }}>({new Date(user.createdAt).toLocaleDateString()})</span>
                        </div>

                        <div className="invite-user-status">
                          <span className="status-badge pending">
                            Pending
                          </span>
                        </div>

                        <div className="invite-user-actions">

                          {
                            user.resendCount >= 3 ? (

                              <button
                                className="limit-btn"
                                disabled
                              >
                                Limit Reached
                              </button>

                            ) : (

                              <>

                                {
                                  (
                                    (user.type === "email" &&
                                      !user.emailOptOut) ||

                                    (user.type === "sms" &&
                                      !user.smsOptOut) ||

                                    (user.type === "whatsapp" &&
                                      !user.whatsappOptOut)
                                  ) ? (

                                    <button
                                      className="resend-btn"
                                      onClick={() =>
                                        handleResendInvite(user)
                                      }
                                    >
                                      Resend
                                      ({user.resendCount}/3)
                                    </button>

                                  ) : (

                                    <button
                                      className="optedout-btn"
                                      disabled
                                    >
                                      Opted Out
                                    </button>

                                  )
                                }

                              </>
                            )
                          }

                        </div>


                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
        </div>

      )}
    </div>
  );
};

export default UsersPage;